import {
  type PropsWithChildren,
  useEffect,
  useRef,
  useState
} from "react";
import type { GoOfflineConfig, GoOfflineController } from "../types";
import { GoOfflineContext } from "./context";
import { getOfflineSnapshot, subscribeToOfflineStatus } from "../core/offline-status";
import { promptForInstall, subscribeToInstallPrompt } from "../core/pwa-install";
import { setupOffline } from "../core/setup-offline";

export interface GoOfflineProviderProps extends PropsWithChildren {
  config?: GoOfflineConfig;
}

export function GoOfflineProvider({ children, config }: GoOfflineProviderProps) {
  const [isOnline, setIsOnline] = useState(getOfflineSnapshot().isOnline);
  const [isInstallable, setIsInstallable] = useState(false);
  const [controller, setController] = useState<GoOfflineController | null>(null);
  const controllerRef = useRef<GoOfflineController | null>(null);
  const configKey = JSON.stringify(config ?? {});

  useEffect(() => {
    let disposed = false;

    setupOffline(config)
      .then((nextController) => {
        if (disposed) {
          nextController.destroy();
          return;
        }

        controllerRef.current = nextController;
        setController(nextController);
      })
      .catch((error) => {
        config?.callbacks?.onError?.(error);
      });

    return () => {
      disposed = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [config, configKey]);

  useEffect(() => subscribeToOfflineStatus((snapshot) => setIsOnline(snapshot.isOnline)), []);
  useEffect(() => subscribeToInstallPrompt(setIsInstallable), []);

  const value = {
    isOnline,
    isOffline: !isOnline,
    isInstallable,
    controller,
    promptInstall: promptForInstall,
    update: () => controller?.update() ?? Promise.resolve()
  };

  return <GoOfflineContext.Provider value={value}>{children}</GoOfflineContext.Provider>;
}
